/** A VENUE'S OWN PUBLIC JOIN LINK — the page a player lands on from social
 *  media, the venue's website or a receipt.
 *
 *  It shows that venue's branding, asks for an email, a screen name and a
 *  4-digit PIN, and nothing else. A new player gets that venue's "Link" badge;
 *  a player who already has an account simply signs in with their email and PIN
 *  and gains the badge, never a second account.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { GoalTapelligenceFooter } from '../../components/goalgame/GoalTapelligenceFooter';

const ORIGIN = window.location.origin;
const DEVICE_KEY = 'patron_device_id';

const deviceId = () => {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
        id = (crypto.randomUUID && crypto.randomUUID()) || String(Math.random()).slice(2);
        localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
};

export default function GoalVenueJoin() {
    const { token } = useParams();
    const { t, i18n } = useTranslation();
    const [venue, setVenue] = useState(null);
    const [brand, setBrand] = useState(null);
    const [form, setForm] = useState({ email: '', screen_name: '', pin: '' });
    const [consent, setConsent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${ORIGIN}/api/goal-game/public/join/${token}`);
            setVenue(res.data);
            const b = await axios.get(
                `${ORIGIN}/api/goal-game/public/${res.data.card_id}/brand?lang=${i18n.language || 'en'}`);
            setBrand(b.data);
        } catch (_e) {
            setVenue({ open: false, venue_name: '' });
        }
    }, [token, i18n.language]);

    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            const res = await axios.post(`${ORIGIN}/api/goal-game/public/join/${token}`, {
                ...form, consent, device_id: deviceId(), lang: i18n.language || 'en',
            });
            setDone(res.data);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 429) setError(t('goalGame.joinTooMany'));
            else if (detail?.reason === 'phone_taken') {
                setError(t('goalGame.phoneTaken', { player: detail.player || '' }));
            } else if (detail === 'wrong_pin') setError(t('goalGame.pinMismatch'));
            else setError(t('goalGame.chatFailed'));
        } finally { setBusy(false); }
    };

    if (!venue) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-slate-950 text-white">
                <Loader2 className="animate-spin" />
                <GoalTapelligenceFooter />
            </div>
        );
    }

    const c = brand?.colors || {};
    const bg = c.bg || '#0E1117';
    const text = c.text || '#F8FAFC';
    const accent = c.accent || '#E11D48';
    const logo = brand?.logo_url
        ? (brand.logo_url.startsWith('http') ? brand.logo_url : `${ORIGIN}${brand.logo_url}`)
        : null;

    return (
        <div className="min-h-screen px-5 py-10" style={{ backgroundColor: bg, color: text }}
             data-testid="goal-join-page">
            <div className="mx-auto w-full max-w-sm space-y-5">
                {logo && <img src={logo} alt="" className="h-12 object-contain" data-testid="goal-join-logo" />}

                {!venue.open ? (
                    <div data-testid="goal-join-closed">
                        <h1 className="text-2xl font-bold">{t('goalGame.joinClosed')}</h1>
                        <p className="text-sm opacity-70">{t('goalGame.joinClosedHint')}</p>
                    </div>
                ) : done ? (
                    <div className="space-y-4" data-testid="goal-join-done">
                        <h1 className="text-2xl font-bold">
                            {t('goalGame.joinWelcome', { venue: done.venue_name })}
                        </h1>
                        <a href={`/goal/${done.card_id}`} data-testid="goal-join-open-game"
                           className="inline-flex min-h-[44px] items-center rounded-full px-5 text-sm font-semibold"
                           style={{ backgroundColor: accent, color: '#ffffff' }}>
                            {t('goalGame.joinOpenGame')}
                        </a>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4" data-testid="goal-join-form">
                        <div>
                            <h1 className="text-2xl font-bold" data-testid="goal-join-title">
                                {t('goalGame.joinTitle', { venue: venue.venue_name })}
                            </h1>
                            <p className="text-sm opacity-70">{t('goalGame.joinHint')}</p>
                        </div>
                        <input type="email" required value={form.email} data-testid="goal-join-email"
                               onChange={(e) => setForm({ ...form, email: e.target.value })}
                               placeholder={t('goalGame.joinEmail')}
                               className="min-h-[44px] w-full rounded-xl border border-white/15 bg-white/5 px-4 text-sm outline-none" />
                        <input type="text" required value={form.screen_name} data-testid="goal-join-name"
                               onChange={(e) => setForm({ ...form, screen_name: e.target.value })}
                               placeholder={t('goalGame.joinName')}
                               className="min-h-[44px] w-full rounded-xl border border-white/15 bg-white/5 px-4 text-sm outline-none" />
                        <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={4} required
                               value={form.pin} data-testid="goal-join-pin"
                               onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '') })}
                               placeholder={t('goalGame.joinPin')}
                               className="min-h-[44px] w-full rounded-xl border border-white/15 bg-white/5 px-4 text-sm outline-none" />
                        <label className="flex items-start gap-2 text-xs opacity-80">
                            <input type="checkbox" checked={consent} data-testid="goal-join-consent"
                                   onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
                            <span>{brand?.patron_consent_text
                                || t('goalGame.joinConsentTwo', { venue: venue?.venue_name || t('goalGame.senderVenue') })}</span>
                        </label>
                        {error && (
                            <p className="text-sm font-semibold text-amber-300" data-testid="goal-join-error">{error}</p>
                        )}
                        <button type="submit" disabled={busy} data-testid="goal-join-submit"
                                className="min-h-[44px] w-full rounded-full text-sm font-semibold disabled:opacity-50"
                                style={{ backgroundColor: accent, color: '#ffffff' }}>
                            {busy ? '…' : t('goalGame.joinSubmit')}
                        </button>
                    </form>
                )}
                {/* The same small Tapelligence line every player screen has. */}
                <GoalTapelligenceFooter />
            </div>
        </div>
    );
}
